import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'redux-form';
import { Query } from 'react-apollo';
import { get } from 'lodash';
import {
  required,
  phone,
  email,
  applicationTerm,
  parseNumber,
  normalizePhone,
  validateEnrollmentNumbers,
  validateSchoolMarketSchoolType,
  uniqueSchoolMarketTeamMember,
  requiredTrue,
} from '../../helpers';
import getLabel from '../../helpers/getLabel';
import TextField from '../TextField';
import RadioButtonGroup from '../RadioButtonGroup';
import CheckboxGroup from '../CheckboxGroup';
import SchoolSelectInput from '../SchoolSelectInput';
import { allSchoolsQuery } from '../../apollo';
import DropDownMenu from '../DropDownMenu';
import ApplicationActions from '../ApplicationActions';
import sortGradesServed from '../../helpers/sortGradesServed';
import BackgroundVerification from '../BackgroundVerification';
import DistrictDropDownMenu from '../DistrictDropDownMenu';
import TextArea from '../TextArea';
import NonDiscriminationStament from '../NonDiscriminationStament';
import GeneralAgreement2024 from '../GeneralAgreement2024';
import GeneralAgreement from '../GeneralAgreement';
import Checkbox from '../Checkbox';
import SchoolMarketAgreement2024 from '../SchoolMarketAgreement2024';
import SchoolMarketAgreement from '../SchoolMarketAgreement';
import moment from 'moment';
import CountyList from '../../constants/CountyList';
import Button from '@material-ui/core/Button';
import PrintIcon from '@material-ui/icons/Print';

const ApplicationSchoolMarketStepOne = (props) => {
  const {
    data,
    submit,
    handleSubmit,
    change,
    readOnly,
    termYear,
    termsAvailable,
    configSchoolMarket,
    termsForYear,
    invalid,
    submitFailed,
    propData,
    enums: {
      gradesServed,
      schoolTypes,
      operatingFrequencies,
      preferredDistributionDays,
      preferredDistributionTimes,
      schoolMarketTypes,
      termTypes,
    },
  } = props;

  const [schoolSelectionState, setSchoolSelectionState] = useState({
    districtId: null,
    schoolType: null,
    shouldFetch: false,
    hasFetched: false
  });

  const [hasExistingApplication, setHasExistingApplication] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);

  const checkExistingApplication = (selectedSchool) => {
    if (!selectedSchool || !termsForYear.length || !selectedTerm) return false;
    const currentYear = termsForYear[0] && termsForYear[0].year;
    
    const existingApp = selectedSchool.applicationsSchoolMarket.find(app => {
      if (!app.createdAt) return false;
      return app.term.year == currentYear && app.term.type === selectedTerm.type;
    });
  
    if (existingApp) {
      setHasExistingApplication({
        exists: true,
        status: existingApp.status,
        submittedBy: existingApp.submittedBy
      });
      return true;
    }
  
    setHasExistingApplication(false);
    return false;
  };

  const { schoolEnabled, summerEnabled } = configSchoolMarket.configSchoolMarket;
  const dataCopy = data ? data : propData;
  const termOptions = termsForYear.map(({ termId, type, termFormatted }) => ({
    value: termId,
    label: (
      <span>
        {getLabel(termTypes.options, type)}
        <span className="term-text">{termFormatted}</span>
      </span>
    ),
  }));

  // Get the saved termId for read-only display
  const savedTermId = data && data.applicationSchoolMarket && data.applicationSchoolMarket.termId;

  const dynamicOperatingFrequencies = operatingFrequencies.options.map(({ value, label }) => {
    const withFirstYear = label.replace(' -', `, ${termYear} -`);
    const withSecondYear = withFirstYear.replace(' (', `, ${withFirstYear.includes(' –')? termYear: Number(termYear) + 1} (`);
    return {
      value,
      label: withSecondYear,
    };
  });

  const filterOperatingFrequencies = (selectedTerm, frequencies) => {
    if (!selectedTerm || !frequencies) return [];
  
    // If term type is SUMMER, only show MAY_JULY options
    if (selectedTerm.type === 'SUMMER') {
      return frequencies.filter(freq => 
        freq.value.startsWith('MAY_JULY')
      );
    }
  
    // If term type is SCHOOL, show AUGUST options
    if (selectedTerm.type === 'SCHOOL') {
      return frequencies.filter(freq => 
        freq.value.startsWith('AUGUST')
      );
    }
  
    return frequencies;
  };

  const educatorNameMatch = (value, allValues) => {

    if ((dataCopy.applicationSchoolMarket.educator.firstName+ " " +dataCopy.applicationSchoolMarket.educator.lastName).trim() !== value.trim()) {
      return 'Name Doesn\'t Match';
    }
  
    return undefined;
  };

  const isPrev2024 = () => {
    const date = moment(dataCopy.applicationSchoolMarket.submittedAt);

    const data2 = dataCopy.applicationSchoolMarket;
    const year = new Date().getFullYear() - 1;
    const isCont = data2.school.applicationsSchoolMarket ?
    data2.school.applicationsSchoolMarket.find(el => el.submittedAt && el.submittedAt.includes(year.toString()) && !el.isContinuation && el.status == "APPROVED"):
      false

    return (!!dataCopy.applicationSchoolMarket.submittedAt && ((date.year() < 2024 || date.month() < 7) && date.year() <= 2024)) || isCont;
  };

  const handleDownloadMOU = () => {
    window.print();
  };

  useEffect(() => {
    if (data && data.applicationSchoolMarket && data.applicationSchoolMarket.school && 
      data.applicationSchoolMarket.school.districtId && data.applicationSchoolMarket.school.type){
      setSchoolSelectionState({
        districtId: data.applicationSchoolMarket.school.districtId,
        schoolType: data.applicationSchoolMarket.school.type,
        shouldFetch: false,
        hasFetched: false
      })
    }
  },[data && data.applicationSchoolMarket && data.applicationSchoolMarket.school])

  // Set termId value only in read-only mode when we have saved data
  useEffect(() => {
    if (readOnly && savedTermId) {
      change('termId', savedTermId);
    }
  }, [readOnly, savedTermId, change]);

  // Auto-trigger print if URL parameter is present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('print') === 'true') {
      // Wait for page and images to fully load before printing
      const triggerPrint = () => {
        // Check if all images are loaded
        const images = document.querySelectorAll('img');
        const allImagesLoaded = Array.from(images).every(img => img.complete);

        if (allImagesLoaded && document.readyState === 'complete') {
          // Add a small delay to ensure rendering is complete
          setTimeout(() => {
            window.print();
          }, 500);
        } else {
          // If not ready, check again in a bit
          setTimeout(triggerPrint, 300);
        }
      };

      // Start checking after initial load
      if (document.readyState === 'complete') {
        triggerPrint();
      } else {
        window.addEventListener('load', triggerPrint);
        return () => window.removeEventListener('load', triggerPrint);
      }
    }
  }, []);

  const handleDistrictChange = (event, newValue) => {
    setSchoolSelectionState(prev => {
      const shouldFetch = Boolean(newValue && prev.schoolType) && !prev.hasFetched;
      return {
        ...prev,
        districtId: newValue,
        shouldFetch: true,
        hasFetched: shouldFetch ? true : prev.hasFetched
      };
    });
    change('school.name', null);
  };

  const handleSchoolTypeChange = (event, newValue) => {
    setSchoolSelectionState(prev => {
      const shouldFetch = Boolean(newValue && prev.districtId) && !prev.hasFetched;
      return {
        ...prev,
        schoolType: newValue,
        shouldFetch: true,
        hasFetched: shouldFetch ? true : prev.hasFetched
      };
    });
    return change('school.name', null);
  };

  const renderSchoolSelect = ({ loading, error, data }) => {
    const schoolOptions = get(data, 'allSchools', []).map(school => ({
      school,
      label: school.name,
    }));
    
    return (
      <Field
        label={<label htmlFor="First" className="field-label">School Name</label>}
        component={SchoolSelectInput}
        options={schoolOptions}
        validate={required}
        districtId={schoolSelectionState.districtId}
        schoolType={schoolSelectionState.schoolType}
        change={change}
        loading={loading}
        name="school.name"
        placeholder={
          loading ? 'Loading schools...' :
          error ? 'Error loading schools' :
          'Select a school'
        }
        className="text-field w-input"
        disabled={readOnly || !termsAvailable || loading}
        onChangeAux={(value) => {
          checkExistingApplication(value);
        }}
      />
    );
  };

  return (
    <Form onSubmit={handleSubmit(submit)} id="email-form" className="program-form">
      <h4 className="purple-headline boxed-in">Application Type</h4>
      {
        !termsForYear.length ? (
          <span style={{ color: 'red' }}>
            We are not currently accepting applications for this year.
          </span>
        ) : (
          <Field
            name="termId"
            options={termOptions}
            component={RadioButtonGroup}
            className="radio-field w-radio"
            disabled={readOnly || !termsAvailable}
            onChange={(event, newValue) => {
              const term = termsForYear.find(term => term.termId === newValue);
              setSelectedTerm(term);
            }}
            validate={value => applicationTerm(value, termsForYear, summerEnabled, schoolEnabled)}
          />
        )
      }
      {(!(data && data.applicationSchoolMarket && (data.applicationSchoolMarket.status === 'APPROVED')) && !termsAvailable && Boolean(termsForYear.length)) && (
        <span style={{ color: 'red', display: 'block' }}>
          We are not currently accepting new applications.
        </span>
      )}
      <div className="site-information">
        <h4 className="purple-headline boxed-in">Initial School Information</h4>
        <div className="radio-question">
          <label htmlFor="First-3" className="field-label">Did your school participate in this program last year?</label>
          <Field
            name="new"
            options={[
              {
                value: false,
                label: 'Yes',
              },
              {
                value: true,
                label: 'No',
              },
            ]}
            component={RadioButtonGroup}
            className="radio-field inline-radio w-radio"
            parse={val => val === 'true'}
            disabled={readOnly || !termsAvailable}
            validate={required}
          />
        </div>
        <label htmlFor="First" className="field-label">School Type (School Market is not available for Elementary Schools)</label>
        <Field
          name="school.type"
          validate={[required, validateSchoolMarketSchoolType]}
          options={schoolTypes.options}
          component={RadioButtonGroup}
          className="radio-field inline-radio w-radio"
          disabled={readOnly || !termsAvailable}
          onChange={handleSchoolTypeChange}
        />
        <div className="field-combo">
          <Field
            name="school.districtId"
            className="text-field w-select"
            component={DistrictDropDownMenu}
            label={<label htmlFor="First-3" className="field-label">School District</label>}
            validate={required}
            disabled={readOnly || !termsAvailable}
            onChange={handleDistrictChange}
          />
        </div>
        {(schoolSelectionState.districtId && schoolSelectionState.schoolType) && (
          <>  
        <div className="field-combo-wrapper dropdown-select">
          <div className="field-combo">
            <Query query={allSchoolsQuery} variables={{
              districtId: schoolSelectionState.districtId,
              schoolType: schoolSelectionState.schoolType
            }} fetchPolicy="network-only">
              {({ loading, error, data }) => {
                const schoolOptions = get(data, 'allSchools', []).map(school => ({
                  school,
                  label: school.name,
                }));
                return (
                  <Field
                    label={<label htmlFor="First" className="field-label">School Name</label>}
                    component={SchoolSelectInput}
                    options={schoolOptions}
                    validate={required}
                    change={change}
                    loading={loading}
                    districtId={schoolSelectionState.districtId}
                    schoolType={schoolSelectionState.schoolType}
                    name="school.name"
                    placeholder={error ? 'Error loading schools, please refresh.' : 'Enter School Name'}
                    className="text-field w-input"
                    disabled={readOnly || !termsAvailable}
                    onChangeAux={(value) => {
                      checkExistingApplication(value);
                    }}
                  />
                );
              }}
            </Query>
          </div>
          
        </div>
        {!hasExistingApplication && <>
        <div className="textarea-combo">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">Organization Type</label>}
              component={TextField}
              name="organizationType"
              className="text-field w-input"
              placeholder=""
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-textarea half">
            <Field
              name="nonProfitOrganizations"
              component={TextArea}
              maxLength={255}
              className="field-textarea w-input"
              label={<label htmlFor="First-3" className="field-label">If you are a 501(c)(3) organization, please list the schools and/or school district (including address), you are partnered with below. If you are a school, please skip this section.</label>}
              disabled={readOnly || !termsAvailable}
            />
          </div>
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">Address Line 1</label>}
              component={TextField}
              validate={required}
              name="school.address1"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Address Line 2 (optional)</label>}
              component={TextField}
              name="school.address2"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">City</label>}
              component={TextField}
              validate={required}
              name="school.city"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">County</label>}
              component={DropDownMenu}
              validate={required}
              options={CountyList}
              name="school.county"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">State</label>}
              component={TextField}
              validate={required}
              name="school.state"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Zip</label>}
              component={TextField}
              validate={required}
              name="school.zip"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
            />
          </div>
        </div>
        <div className="checklist-question">
          <label htmlFor="First-3" className="field-label">Grades Served (check all that apply)</label>
          <Field
            name="school.gradesServed"
            validate={required}
            options={sortGradesServed(gradesServed)}
            component={CheckboxGroup}
            className="checkbox-inline w-checkbox _7"
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="First-3" className="field-label">Total Enrollment</label>}
              component={TextField}
              validate={required}
              name="numTotalEnrollment"
              className="text-field w-input"
              parse={parseNumber}
              maxLength={4}
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Number of Students Receiving Reduced Lunch</label>}
              component={TextField}
              validate={[required, validateEnrollmentNumbers]}
              name="numReducedLunch"
              className="text-field w-input"
              parse={parseNumber}
              maxLength={4}
              disabled={readOnly || !termsAvailable}
            />
          </div>
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Number of Students Receiving Free Lunch</label>}
              component={TextField}
              validate={[required, validateEnrollmentNumbers]}
              name="numFreeLunch"
              className="text-field w-input"
              parse={parseNumber}
              maxLength={4}
              disabled={readOnly || !termsAvailable}
            />
          </div>
        </div></>}
        </>)}
      </div>
      {(schoolSelectionState.districtId && schoolSelectionState.schoolType) && !hasExistingApplication && 
      <>
        <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            name="marketType"
            className="select-field w-select"
            component={DropDownMenu}
            label={(
              <>
                <label htmlFor="Last-3" className="field-label" style={{ marginTop: 0 }}>Your Market Type</label>
                <p className="market-type-disclaimer">
                *Traditional distribution models have
                been modified to ensure social distancing
                and follow safety protocols to protect
                our clients, staff, partners, and volunteers
                </p>
              </>
            )}
            placeholder="Select Type"
            options={schoolMarketTypes.options}
            validate={required}
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
        <Field
          name="operatingFrequency"
          className="select-field w-select"
          component={DropDownMenu}
          label={<label htmlFor="Last-3" className="field-label">Your Market Distributions Frequency</label>}
          placeholder="Select Frequency"
          options={filterOperatingFrequencies(selectedTerm, dynamicOperatingFrequencies)}
          validate={required}
          disabled={readOnly || !termsAvailable}
        />
        </div>
      </div>
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            name="preferredDistributionDay"
            className="select-field w-select"
            component={DropDownMenu}
            label={<label htmlFor="Last-3" className="field-label">Preferred Market Distribution Day (subject to availability)</label>}
            placeholder="Select Day"
            options={preferredDistributionDays.options && preferredDistributionDays.options.length ? preferredDistributionDays.options.filter(el => el.value != 'MONDAY'): []}
            validate={required}
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
          <Field
            name="preferredDistributionTime"
            className="select-field w-select"
            component={DropDownMenu}
            label={<label htmlFor="Last-3" className="field-label">Preferred Market Distribution Time</label>}
            placeholder="Select Time"
            options={preferredDistributionTimes.options.filter(el => el.value != 'DISTRIBUTION_11AM_2PM')}
            validate={required}
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            name="secondaryPreferredDistributionDay"
            className="select-field w-select"
            component={DropDownMenu}
            label={<label htmlFor="Last-3" className="field-label">Secondary Preferred Market Distribution Day (subject to availability)</label>}
            placeholder="Select Day"
            options={preferredDistributionDays.options && preferredDistributionDays.options.length ? preferredDistributionDays.options.filter(el => el.value != 'MONDAY'): []}
            validate={required}
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
          <Field
            name="secondaryPreferredDistributionTime"
            className="select-field w-select"
            component={DropDownMenu}
            label={<label htmlFor="Last-3" className="field-label">Secondary Preferred Market Distribution Time</label>}
            placeholder="Select Time"
            options={preferredDistributionTimes.options.filter(el => el.value != 'DISTRIBUTION_11AM_2PM')}
            validate={required}
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <div className="point-of-contact">
        <h4 className="purple-headline boxed-in">Primary Point of Contact</h4>
        <p>
          Houston ISD Schools: The Primary Point of Contact
          (school liaison) is required to provide an e-signature
          for this application to be considered. An email message
          will be sent to the Primary Point of Contact email that
          is provided. The Primary Point of Contact must
          click the signature link in the email and
          follow the instructions to provide their signature.
        </p>
        <div className="field-combo-wrapper">
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="First-3" className="field-label">First Name</label>}
              component={TextField}
              validate={required}
              name="school.primaryPointOfContact.firstName"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Last Name</label>}
              component={TextField}
              validate={required}
              name="school.primaryPointOfContact.lastName"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
          <div className="field-combo thirds">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Job Title (optional)</label>}
              component={TextField}
              name="school.primaryPointOfContact.title"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">Phone</label>}
              component={TextField}
              validate={[required, phone]}
              normalize={normalizePhone}
              name="school.primaryPointOfContact.phone"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Email</label>}
              component={TextField}
              validate={[required, email, uniqueSchoolMarketTeamMember]}
              name="school.primaryPointOfContact.email"
              className="text-field w-input"
              placeholder="e.g. educator@school.com"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
        </div>
      </div>
      <div className="coordinator">
        <h4 className="purple-headline boxed-in">Principal</h4>
        <p>
          Non-Houston ISD Schools: The Principal is required to provide an
          e-signature for this application to be considered. An email
          message will be sent to the Principal email that is provided.
          The Principal must click the signature link in the email and
          follow the instructions to provide their signature.
        </p>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">First Name</label>}
              component={TextField}
              validate={required}
              name="school.principal.firstName"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Last Name</label>}
              component={TextField}
              validate={required}
              name="school.principal.lastName"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
        </div>
        <div className="field-combo-wrapper">
          <div className="field-combo">
            <Field
              label={<label htmlFor="First-3" className="field-label">Phone</label>}
              component={TextField}
              validate={[required, phone]}
              normalize={normalizePhone}
              name="school.principal.phone"
              className="text-field w-input"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
          <div className="field-combo">
            <Field
              label={<label htmlFor="Last-3" className="field-label">Email</label>}
              component={TextField}
              validate={[required, email, uniqueSchoolMarketTeamMember]}
              name="school.principal.email"
              className="text-field w-input"
              placeholder="e.g. educator@school.com"
              disabled={readOnly || !termsAvailable}
              autoComplete="input-unsupported"
            />
          </div>
        </div>
      </div>
      <BackgroundVerification {...props} />
      {dataCopy.applicationSchoolMarket && dataCopy.applicationSchoolMarket.school && <>
        <h4 className="purple-headline boxed-in">The Agreement</h4>
        <p>
          This document is an agreement made between Houston Food Bank’s Teachers Aid and your school,
          that by participating in the school supply program, the school agrees to the following:
        </p>
        <div className="mou-to-print">
          {isPrev2024() ? <GeneralAgreement data={dataCopy.applicationSchoolMarket} />:<GeneralAgreement2024 data={dataCopy.applicationSchoolMarket} />}
        </div>

        <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'left', paddingLeft: '41pt' }} className="no-print">
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadMOU}
            startIcon={<PrintIcon />}
          >
            Download MOU
          </Button>
        </div>

      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={
                (
                  <Fragment>
                    <label htmlFor="Last" className="field-label">
                      Agreement Sign
                    </label>
                  </Fragment>
                )
              }
            component={TextField}
            placeholder="Enter Your Name"
            validate={[required]}
            name="nameSignFirst"
            disabled={readOnly || !termsAvailable}
            className="text-field w-input"
          />
        </div>
        <div className="field-combo">
          <Field
            label={<strong>I Agree</strong>}
            name="agreeToProgramPurpose"
            validate={requiredTrue}
            component={Checkbox}
            className="checkbox-inline"
            disabled={readOnly || !termsAvailable}
            {...{
              style: {paddingBottom: 35}
            }}
          />
        </div>
      </div>
      <NonDiscriminationStament data={dataCopy.applicationSchoolMarket} />
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={
                (
                  <Fragment>
                    <label htmlFor="Last" className="field-label">
                      Nondiscrimination Sign
                    </label>
                  </Fragment>
                )
              }
            component={TextField}
            placeholder="Enter Your Name"
            validate={[required]}
            name="nameSignSecond"
            disabled={readOnly || !termsAvailable}
            className="text-field w-input"
          />
        </div>
        <div className="field-combo">
          <Field
            label={<strong>I Agree</strong>}
            name="agreeNondiscrimination"
            validate={requiredTrue}
            component={Checkbox}
            className="checkbox-inline"
            disabled={readOnly || !termsAvailable}
            {...{
              style: {paddingBottom: 35}
            }}
          />
        </div>
      </div>
      {isPrev2024() ? <SchoolMarketAgreement />:<SchoolMarketAgreement2024 />}
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={
                (
                  <Fragment>
                    <label htmlFor="Last" className="field-label">
                      Program Sign
                    </label>
                  </Fragment>
                )
              }
            component={TextField}
            placeholder="Enter Your Name"
            validate={[required]}
            name="nameSignThird"
            disabled={readOnly || !termsAvailable}
            className="text-field w-input"
          />
        </div>
        <div className="field-combo">
          <Field
            label={<strong>I Agree</strong>}
            name="agreeToProgramResponsibilities"
            validate={requiredTrue}
            component={Checkbox}
            className="checkbox-inline"
            disabled={readOnly || !termsAvailable}
            {...{
              style: {paddingBottom: 35}
            }}
          />
        </div>
      </div></>}
      </>}
      {hasExistingApplication && (
        <div style={{ 
          color: '#664d03', 
          padding: '15px', 
          margin: '20px 0', 
          border: '1px solid #ffecb5',
          borderRadius: '4px',
          backgroundColor: '#fff3cd'
        }}>
          <p style={{ margin: '0 0 10px 0' }}>
            This school already has a School Market application in progress for {termsForYear[0] && termsForYear[0].year}.
          </p>
          <p style={{ margin: '0' }}>
              In Progress Application Started By: 
              <a href={`mailto:${hasExistingApplication.submittedBy}`} className="paragraph-link">
                {hasExistingApplication.submittedBy}
              </a>
          </p>
          If assistance is needed, please contact:
          <a href={`mailto:schoolmarket@houstonfoodbank.org`} className="paragraph-link">
            schoolmarket@houstonfoodbank.org
          </a>
        </div>
      )}
      {!hasExistingApplication && <div className="next-step">
        <ApplicationActions {...props} />
      </div>}
      {
        (invalid && submitFailed) && (
          <span
            style={{
              color: 'red',
            }}
          >
            You have required field(s) that are missing. Please see above.
          </span>
        )
      }
    </Form>
  );
};

ApplicationSchoolMarketStepOne.propTypes = {
  submit: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  termYear: PropTypes.string.isRequired,
  termsAvailable: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  enums: PropTypes.object.isRequired,
  configSchoolMarket: PropTypes.object.isRequired,
  termsForYear: PropTypes.array.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  data: PropTypes.object,
  propData: PropTypes.object,
};

ApplicationSchoolMarketStepOne.defaultProps = {
  readOnly: false,
  data: null,
};

export default ApplicationSchoolMarketStepOne;